<template>
    <Form class="form p-4">
        <div class="pb-4">
            <span class="Paragraph-Medium">{{ mode === "add" ? "Add New User" : "Edit User" }}</span>
        </div>
        <div class="grid grid-nogutter gap-4 mb-4">
            <div class="col-12 md:col-3 flex flex-column justify-content-start gap-1">
                <span class="Label">User details</span>
                <span class="Caption">View & update user details</span>
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-3">
                <label for="Name" class="Label align-self-start">Name</label>
                <div class="w-full">
                    <IconField class="w-full">
                        <InputIcon class="pi pi-user" />
                        <InputText placeholder="Enter name here" size="small" variant="filled" class="w-full"
                            :invalid="!!errors.username" v-model="form.username" @input="clearError('username')" />
                    </IconField>
                    <small class="field-error">{{ errors.username }}</small>
                </div>
                <label for="email" class="Label align-self-start">Email</label>
                <div class="w-full">
                    <IconField class="w-full">
                        <InputIcon class="pi pi-envelope" />
                        <InputText placeholder="Enter email here" size="small" variant="filled" class="w-full"
                            :invalid="!!errors.email" v-model="form.email" @input="clearError('email')" />
                    </IconField>
                    <small class="field-error">{{ errors.email }}</small>
                </div>
            </div>
        </div>
        <Divider class="w-full py-2" />
        <div class="grid grid-nogutter gap-4 mb-4">
            <div class="col-12 md:col-3 flex flex-column justify-content-start gap-1">
                <span class="Label">User security</span>
                <span class="Caption">View & update user security</span>
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-3">
                <label for="password" class="Label align-self-start mb-2">Password</label>
                <div class="w-full">
                    <IconField class="w-full">
                        <InputIcon class="pi pi-lock" />
                        <Password v-model="form.password" toggleMask placeholder="password" size="small"
                            variant="filled" class="w-full" autocomplete="current-password" :feedback="false"
                            :invalid="!!errors.password" @input="clearError('password')" />
                    </IconField>
                    <small class="field-error">{{ errors.password }}</small>
                </div>
                <label for="role" class="Label align-self-start mb-2">Role</label>
                <div class="w-full">
                    <div class="flex align-items-center gap-6 ">
                        <div class="flex align-items-center gap-2">
                            <Checkbox v-model="selectedRole" class="accent-checkbox" inputId="superAdmin" binary
                                :true-value="'super_admin'" :false-value="null"
                                :checked="selectedRole === 'super_admin'"
                                @change="selectedRole = 'super_admin'; clearError('role')" />
                            <label for="superAdmin" class="Caption text-left">Super Admin</label>
                        </div>

                        <div class="flex align-items-center gap-2">
                            <Checkbox v-model="selectedRole" class="accent-checkbox" inputId="admin" binary
                                :true-value="'admin'" :false-value="null" :checked="selectedRole === 'admin'"
                                @change="selectedRole = 'admin'; clearError('role')" />
                            <label for="admin" class="Caption text-left">Admin</label>
                        </div>
                    </div>
                    <small class="field-error">{{ errors.role }}</small>
                </div>
                <!-- <label for="confirmPassword" class="Label align-self-start mb-2">Status</label>
                <div class=" flex flex-wrap justify-content-start align-items-center gap-2">
                    <span class="Caption">Disable</span>
                    <ToggleSwitch v-model="form.enabled" />
                    <span class="Caption">Enable</span>
                </div> -->
            </div>
        </div>
        <div class="flex justify-content-end gap-4 mt-6">
            <Button :label="mode === 'add' ? 'Add' : 'Update'" class="accent-button"
                @click="mode === 'add' ? addUser() : updateUser()" />
            <Button label="Cancel" class="accent-button-transparent" @click="handleCancel" />
        </div>
    </Form>
</template>
<script setup>
import { defineEmits, ref, watch, defineProps } from "vue";
import { Form } from "@primevue/forms";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Password from 'primevue/password';
import Divider from "primevue/divider";
// import ToggleSwitch from 'primevue/toggleswitch';
import Checkbox from "primevue/checkbox";

const props = defineProps({
    adminDetails: Object,
    mode: { type: String, default: "add" },
});

const emit = defineEmits(["add-user", "update-user", "cancel"]);

const form = ref({
    _id: "",
    username: "",
    email: "",
    password: "",
    role: "",
    enabled: true,
});
const selectedRole = ref("");

const errors = ref({
    username: "",
    email: "",
    password: "",
    role: "",
});

const clearError = (field) => {
    errors.value[field] = "";
};

const validate = () => {
    errors.value = { username: "", email: "", password: "", role: "" };
    let valid = true;

    if (!form.value.username || !form.value.username.trim()) {
        errors.value.username = "Name is required.";
        valid = false;
    }

    if (!form.value.email || !form.value.email.trim()) {
        errors.value.email = "Email is required.";
        valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email.trim())) {
        errors.value.email = "Enter a valid email address.";
        valid = false;
    }

    // Password is required when adding a user; optional when editing.
    if (props.mode === "add" && (!form.value.password || !form.value.password.trim())) {
        errors.value.password = "Password is required.";
        valid = false;
    }

    if (!selectedRole.value) {
        errors.value.role = "Please select a role.";
        valid = false;
    }

    return valid;
};

watch(
    () => props.adminDetails,
    (val) => {
        if (val && props.mode === "edit") {
            form.value = { ...val, password: "" };
            selectedRole.value = val.role;
        }
    },
    { immediate: true }
);

const addUser = () => {
    if (!validate()) return;
    form.value.role = selectedRole.value;
    emit("add-user", form.value);
};
const updateUser = () => {
    if (!validate()) return;
    form.value.role = selectedRole.value;
    emit("update-user", form.value);
};
const handleCancel = () => {
    emit('cancel');
};

</script>
<style scoped>
.form {
    width: 100%;
    background-color: white;
}

/* Reserve space so the layout doesn't shift when an error message appears */
.field-error {
    display: block;
    min-height: 1.1rem;
    margin-top: 0.25rem;
    color: #e24c4c;
    font-size: 0.75rem;
    line-height: 1.1rem;
    text-align: left;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }
}
</style>